import { Css, RightPanePanel, TabsWithContent, TabWithContent, Tag, useRightPane, useTestIds } from "@homebound/beam";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatMonthDay } from "src/utils/dates";
import { StringParam, useQueryParams } from "use-query-params";
import { useDraftPlanSchedule } from "./DraftScheduleLayout";
import { DraftTaskDrawer_PlanTaskFragment } from "./DraftTaskDrawer.generated";
import { DraftTradeStatusTag } from "./DraftTradeStatusTag";

type DraftTaskDrawerProps = { taskId: string };

/** The task drawer for the draft schedule, behind `DYNAMIC_SCHEDULES_V2`. */
export function DraftTaskDrawer(props: DraftTaskDrawerProps) {
  const { taskId } = props;
  const { closeRightPane } = useRightPane();
  const query = useDraftPlanSchedule();
  // Keep showing the last draft while a change re-runs the simulation
  const planTasks = (query.data ?? query.previousData)?.draftPlanSchedule.planTasks;
  const task = planTasks?.find((t) => t.id === taskId);

  useEffect(() => {
    // The draft no longer has the task, e.g. it was deleted
    if (planTasks && !task) closeRightPane();
  }, [planTasks, task, closeRightPane]);

  return task ? <DraftTaskDrawerView task={task} /> : <></>;
}

type DraftTaskDrawerViewProps = { task: DraftTaskDrawer_PlanTaskFragment };
type DraftTaskDrawerTab = "schedule" | "trades" | "comments" | "other";

function DraftTaskDrawerView(props: DraftTaskDrawerViewProps) {
  const { task } = props;
  const tid = useTestIds(props, "draftTaskDrawer");
  const { closeRightPane } = useRightPane();
  const [tab, setTab] = useState<DraftTaskDrawerTab>("schedule");
  const { name, tradePartner, startDate, endDate, internalUser, status } = task;
  const details = [
    tradePartner?.name,
    `${formatMonthDay(startDate)} – ${formatMonthDay(endDate)}`,
    internalUser?.name,
  ].compact();
  const tabs: TabWithContent<DraftTaskDrawerTab>[] = [
    { name: "Schedule", value: "schedule", render: () => null },
    { name: "Trades", value: "trades", render: () => null },
    { name: "Comments", value: "comments", render: () => null },
    { name: "Other", value: "other", render: () => null },
  ];

  return (
    <RightPanePanel title={name} secondaryAction={{ label: "Cancel", onClick: closeRightPane }}>
      <div css={Css.df.fdc.gap1.mb2.$}>
        <div css={Css.sm.gray700.$} {...tid.details}>
          {details.join(" · ")}
        </div>
        <div css={Css.df.gap1.$}>
          {tradePartner && <DraftTradeStatusTag task={task} />}
          <Tag text={status.name} {...tid.taskStatus} />
        </div>
      </div>
      <TabsWithContent tabs={tabs} selected={tab} onChange={setTab} />
    </RightPanePanel>
  );
}

/**
 * Opens the task drawer and keeps the open task in `?paneTaskId=`, so the link can be shared.
 *
 * `openTaskId` is only set while the drawer is open.
 */
export function useDraftTaskDrawer() {
  const { openRightPane, isRightPaneOpen } = useRightPane();
  const [{ paneTaskId }, setQueryParams] = useQueryParams({ paneTaskId: StringParam });
  const wasOpen = useRef(isRightPaneOpen);

  useEffect(() => {
    // Drop the task from the URL once the user closes the drawer
    if (wasOpen.current && !isRightPaneOpen) setQueryParams({ paneTaskId: undefined });
    wasOpen.current = isRightPaneOpen;
  }, [isRightPaneOpen, setQueryParams]);

  const openDraftTaskDrawer = useCallback(
    (taskId: string) => {
      setQueryParams({ paneTaskId: taskId });
      openRightPane({ content: <DraftTaskDrawer key={taskId} taskId={taskId} /> });
    },
    [openRightPane, setQueryParams],
  );

  return { openDraftTaskDrawer, paneTaskId, openTaskId: isRightPaneOpen ? paneTaskId : undefined };
}
